import React, { useState } from "react";
import {
  AnalyticsCard,
  AnalyticsTable,
  Cell,
  ColumnDef,
  DropdownFilter,
  LeadsRadialPieChart,
  metricsToRadialSlices,
  TABLE_MIN_HEIGHT,
  TableFooter,
} from "../../common";
import { useMetricsChartQuery, useMetricsQuery } from "src/query/experiences/leadsAndReports/reports";
import { useTableState } from "../../hooks/useTableState";
import { formatDurationWithLabel, safeDivide } from "../../utils/format";
import { ArrangeOption } from "../../common/DropdownFilter";
import styles from "../../common/common.module.scss";

interface MetricsAnalyticsProps {
  storyboardId: string;
  metricType: string;
  tableName: string;
  paginationName: string;
}

const COLUMNS: ColumnDef[] = [
  { key: "name", header: "Name" },
  { key: "views", header: "Views", align: "right" },
  { key: "visitors", header: "Visitors", align: "right" },
  { key: "leads", header: "Leads", align: "right" },
  { key: "total", header: "Total Time", align: "right" },
  { key: "avg", header: "Avg. Time", align: "right" },
  { key: "bounce", header: "Bounce Rate", align: "right" },
];

const renderName = (row: any, type: string) => {
  if (type === "organization") {
    if (row.organization) return row.organization.name;
    if (row.contact?.company) return row.contact.company;
    const email: string = row.contact?.email || "";
    return email.split("@")[1]?.split(".")[0] ?? "-";
  }
  return row.metricType;
};

export const MetricsAnalytics: React.FC<MetricsAnalyticsProps> = ({
  storyboardId,
  metricType,
  tableName,
  paginationName,
}) => {
  const { request, key, page, setPage, chart, setChart, enabled } = useTableState(storyboardId);
  const [viewOption, setViewOption] = useState<ArrangeOption>({
    label: "Views",
    value: "views",
    name: metricType === "organization" ? "organization.name" : "metricType",
  });

  const { data, isLoading } = useMetricsQuery(metricType, request, key, page, enabled);
  const { data: chartData, isLoading: isChartLoading } = useMetricsChartQuery(
    metricType,
    request,
    key,
    enabled,
  );
  const rows = data?.records ?? [];
  const pieData = chartData?.records ?? [];

  const renderChart = () => {
    switch (chart) {
      case "pie_chart_view":
        return (
          <div className={styles.chartFull}>
            <LeadsRadialPieChart
              data={metricsToRadialSlices(pieData, viewOption)}
              measureLabel={viewOption.label}
              height={280}
              loading={isChartLoading}
            />
          </div>
        );
      case "table_view":
        return (
          <AnalyticsTable
            variant="split"
            columns={COLUMNS}
            rows={rows}
            loading={isLoading}
            renderRow={(row: any) => (
              <>
                <Cell headline={renderName(row, metricType)} />
                <Cell headline={row.views} />
                <Cell headline={row.visitors} />
                <Cell headline={row.leads} />
                <Cell headline={formatDurationWithLabel(row.timeSpent).value} />
                <Cell headline={formatDurationWithLabel(safeDivide(row.timeSpent, row.views)).value} />
                <Cell headline={`${Math.round(row.bounceRate)}%`} />
              </>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title={tableName}
      count={data?.totalRecordsCount ?? rows.length}
      currentChart={chart}
      disableView={["graph_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.split}
      action={
        chart === "pie_chart_view" ? (
          <DropdownFilter
            arrangeOption={viewOption}
            handleArrange={(o) => setViewOption((s) => ({ ...s, ...o }))}
            disableOption={[]}
          />
        ) : undefined
      }
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName={paginationName}
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
